import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  AlertTriangle,
  AlertCircle,
  Clock,
  FileSearch,
  GitBranch,
  LogIn,
  RefreshCw,
  ArrowLeft,
  Home,
  ShieldX,
  Timer,
  WifiOff,
} from 'lucide-react'
import { Button } from '@shared/components/ui/button'
import { cn } from '@shared/lib/utils'
import type { ErrorKind } from '@shared/utils/errorHandling'

interface VariantConfig {
  icon: LucideIcon
  title: string
  description: string
  statusCode?: string
  iconBgClass: string
  iconClass: string
}

const VARIANT_CONFIG: Record<ErrorKind, VariantConfig> = {
  not_found: {
    icon: FileSearch,
    title: 'Not found',
    description:
      'The resource you are looking for does not exist or has been removed.',
    statusCode: '404',
    iconBgClass: 'bg-muted',
    iconClass: 'text-muted-foreground',
  },
  forbidden: {
    icon: ShieldX,
    title: 'Access denied',
    description:
      "You don't have permission to view this resource. Contact your administrator.",
    statusCode: '403',
    iconBgClass: 'bg-muted',
    iconClass: 'text-muted-foreground',
  },
  unauthorized: {
    icon: LogIn,
    title: 'Session expired',
    description: 'Please log in again to continue.',
    iconBgClass: 'bg-muted',
    iconClass: 'text-muted-foreground',
  },
  server: {
    icon: AlertTriangle,
    title: 'Something went wrong',
    description:
      'An unexpected error occurred. Please try again or contact support.',
    statusCode: '500',
    iconBgClass: 'bg-destructive/10',
    iconClass: 'text-destructive',
  },
  network: {
    icon: WifiOff,
    title: 'Connection lost',
    description: 'Check your internet connection and try again.',
    iconBgClass: 'bg-muted',
    iconClass: 'text-muted-foreground',
  },
  timeout: {
    icon: Clock,
    title: 'Request timed out',
    description: 'The server took too long to respond. Please try again.',
    iconBgClass: 'bg-amber-100 dark:bg-amber-900/20',
    iconClass: 'text-amber-600',
  },
  validation: {
    icon: AlertCircle,
    title: 'Validation error',
    description: 'Please review and correct the highlighted fields.',
    iconBgClass: 'bg-amber-100 dark:bg-amber-900/20',
    iconClass: 'text-amber-600',
  },
  conflict: {
    icon: GitBranch,
    title: 'Conflict detected',
    description:
      'This resource was modified by another user. Please refresh to see the latest version.',
    iconBgClass: 'bg-amber-100 dark:bg-amber-900/20',
    iconClass: 'text-amber-600',
  },
  rate_limit: {
    icon: Timer,
    title: 'Too many requests',
    description: 'Please wait a moment before trying again.',
    iconBgClass: 'bg-muted',
    iconClass: 'text-muted-foreground',
  },
  unknown: {
    icon: AlertTriangle,
    title: 'Unexpected error',
    description: 'Something went wrong. Please try again or contact support.',
    iconBgClass: 'bg-destructive/10',
    iconClass: 'text-destructive',
  },
}

export interface ErrorStateProps {
  /** Error variant determining icon, title, and messaging */
  variant: ErrorKind
  /** Override the default title */
  title?: string
  /** Override the default description */
  description?: string
  /** Retry callback (shows retry button when provided) */
  onRetry?: () => void
  /** Navigate back callback (shows back button when provided) */
  onBack?: () => void
  /** Navigate home callback (shows home button when provided) */
  onHome?: () => void
  /** Custom action buttons to render instead of defaults */
  actions?: ReactNode
  /** Render compact inline version */
  inline?: boolean
  /** Raw error for dev-mode details display */
  error?: Error | null
  /** Additional CSS classes */
  className?: string
}

export function ErrorState({
  variant,
  title,
  description,
  onRetry,
  onBack,
  onHome,
  actions,
  inline = false,
  error,
  className,
}: ErrorStateProps) {
  const config = VARIANT_CONFIG[variant]
  const Icon = config.icon
  const displayTitle = title || config.title
  const displayDescription = description || config.description

  if (inline) {
    return (
      <div
        className={cn(
          'flex flex-col items-center gap-4 rounded-lg border border-destructive/20 bg-destructive/5 p-6 text-center',
          className
        )}
      >
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full',
            config.iconBgClass
          )}
        >
          <Icon className={cn('h-5 w-5', config.iconClass)} />
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-foreground">{displayTitle}</p>
          <p className="text-xs text-muted-foreground">{displayDescription}</p>
        </div>
        {actions || (
          onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCw className="mr-2 h-3 w-3" />
              Retry
            </Button>
          )
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center py-20 px-4',
        className
      )}
    >
      <div className="mx-auto flex max-w-md flex-col items-center gap-6 text-center">
        <div
          className={cn(
            'flex h-16 w-16 items-center justify-center rounded-full',
            config.iconBgClass
          )}
        >
          <Icon className={cn('h-8 w-8', config.iconClass)} />
        </div>
        <div className="flex flex-col gap-2">
          {config.statusCode && (
            <h1 className="text-4xl font-bold text-foreground">
              {config.statusCode}
            </h1>
          )}
          <p className="text-lg font-medium text-foreground">{displayTitle}</p>
          <p className="text-sm text-muted-foreground">{displayDescription}</p>
        </div>
        {import.meta.env.DEV && error && (
          <div className="w-full rounded-md border border-border bg-muted/50 p-3 text-left">
            <p className="mb-1 text-xs font-medium text-destructive">
              {error.name}
            </p>
            <p className="text-xs text-muted-foreground break-all">
              {error.message}
            </p>
          </div>
        )}
        {actions || (
          <div className="flex gap-3">
            {onRetry && (
              <Button variant="outline" onClick={onRetry}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            )}
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Go Back
              </Button>
            )}
            {onHome && (
              <Button onClick={onHome}>
                <Home className="mr-2 h-4 w-4" />
                Home
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
